import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useBillingClient } from 'client/client-hook';
import { featureFlags } from 'env';
import useAuth from 'hooks/useAuth';
import { SubscriptionFreezeStatus } from 'proto/billing-manager_pb';
import { hasComplimentaryAccess } from 'utils/entitlement';
import { freezeSwitchedOff } from './freezeLock';
import { SubscriptionFreeze, SubscriptionFreezeReply } from './types';
import { withTimeout } from './withTimeout';

const refreshTimeoutMs = 12_000;
const pollDelays = [2_000, 4_000, 8_000, 15_000];
// A resume payment ends on a Stripe webhook, so a paid one is picked up without a reload.
const transitionalStatuses = new Set<number>([
  SubscriptionFreezeStatus.SUBSCRIPTION_FREEZE_STATUS_FREEZING,
  SubscriptionFreezeStatus.SUBSCRIPTION_FREEZE_STATUS_RESUMING,
  SubscriptionFreezeStatus.SUBSCRIPTION_FREEZE_STATUS_RESUME_PAYMENT_PENDING,
  SubscriptionFreezeStatus.SUBSCRIPTION_FREEZE_STATUS_CANCELING
]);

type SubscriptionFreezeContextValue = {
  reply?: SubscriptionFreezeReply;
  freeze?: SubscriptionFreeze;
  loading: boolean;
  error: boolean;
  refresh: () => Promise<void>;
};

const SubscriptionFreezeContext = createContext<SubscriptionFreezeContextValue | undefined>(undefined);

export function SubscriptionFreezeProvider({ children }: { children: ReactNode }) {
  const { user, refreshToken } = useAuth();
  const getBillingClient = useBillingClient();
  const [reply, setReply] = useState<SubscriptionFreezeReply>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const requestVersion = useRef(0);
  const lastStatus = useRef<number>();
  const renewedForPause = useRef<string>();
  const userId = user?.tcId || '';

  const refresh = useCallback(async () => {
    const version = ++requestVersion.current;
    // Flag off: stay mounted so consumers keep working, but never put this
    // request on the critical path of every page load.
    if (!featureFlags.subscriptionFreeze || !userId) {
      setReply(undefined);
      setError(false);
      setLoading(false);
      return;
    }
    try {
      const client = await getBillingClient();
      const nextReply = (await withTimeout(client.getSubscriptionFreeze({ userId } as any), refreshTimeoutMs)) as SubscriptionFreezeReply;
      if (version !== requestVersion.current) return;
      setReply(nextReply);
      setError(false);
    } catch {
      if (version !== requestVersion.current) return;
      setError(true);
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }, [getBillingClient, userId]);

  // Held in a ref so the listeners below never re-subscribe, and so the reset effect can key on the
  // user instead of the callback.
  const latestRefresh = useRef(refresh);
  latestRefresh.current = refresh;

  // Keyed on the user, not on `refresh`: renewing the access token rebuilds the callback, and
  // clearing the reply there blanked the header and the billing page on every renewal.
  useEffect(() => {
    setLoading(true);
    setReply(undefined);
    void latestRefresh.current();
  }, [userId]);

  useEffect(() => {
    const status = reply?.freeze?.status;
    if (status === undefined || !transitionalStatuses.has(status)) return;

    let stopped = false;
    let attempt = 0;
    let timer: number | undefined;
    const schedule = () => {
      timer = undefined;
      if (stopped || document.visibilityState !== 'visible') return;
      timer = window.setTimeout(async () => {
        await latestRefresh.current();
        attempt = Math.min(attempt + 1, pollDelays.length - 1);
        schedule();
      }, pollDelays[attempt]);
    };
    // A hidden tab stops polling; the status has not changed, so only coming back can restart it.
    const resumeWhenVisible = () => {
      if (timer === undefined) schedule();
    };
    schedule();
    document.addEventListener('visibilitychange', resumeWhenVisible);
    return () => {
      stopped = true;
      document.removeEventListener('visibilitychange', resumeWhenVisible);
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [reply?.freeze?.status]);

  // Coming back gives the paid roles back, but the token in hand was issued while switched off: renewed once
  // the pause is seen closing (resumed, failed and undone, or cancelled; billing-manager returns only open
  // ones), so paid actions work without a reload. A page opened afterwards already has a fresh one.
  useEffect(() => {
    const status = reply?.freeze?.status;
    const previous = lastStatus.current;
    lastStatus.current = status;
    const closed = reply !== undefined && status === undefined;
    const resumed = status === SubscriptionFreezeStatus.SUBSCRIPTION_FREEZE_STATUS_RESUMED;
    if ((closed || resumed) && previous !== undefined && previous !== status) {
      refreshToken().catch(() => undefined);
    }
  }, [reply, refreshToken]);

  // Going the other way, the switch-off takes the paid roles away while the token in hand still claims them,
  // so paid pages would open and then fail: renewed once per pause, once it has switched the customer off.
  useEffect(() => {
    const freeze = reply?.freeze;
    if (!freeze || !freezeSwitchedOff(freeze) || !hasComplimentaryAccess(user?.roles)) return;
    if (renewedForPause.current === freeze.id) return;
    renewedForPause.current = freeze.id;
    refreshToken().catch(() => undefined);
  }, [reply, user?.roles, refreshToken]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') void latestRefresh.current();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Memoised: this provider wraps the whole app, so an inline object would re-render the header and
  // every other consumer on each of its own renders.
  const value = useMemo(() => ({ reply, freeze: reply?.freeze, loading, error, refresh }), [reply, loading, error, refresh]);

  return <SubscriptionFreezeContext.Provider value={value}>{children}</SubscriptionFreezeContext.Provider>;
}

export function useSubscriptionFreeze() {
  const value = useContext(SubscriptionFreezeContext);
  if (!value) throw new Error('useSubscriptionFreeze must be used within SubscriptionFreezeProvider');
  return value;
}
